// TabList.js
import React, { useState, useEffect, useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom"; // useLocation -> useSearchParams
import { usePageTitle } from '../../contexts/PageTitleContext';
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/free-mode";
import { FreeMode } from "swiper/modules";
import { Filter } from "../../assets/icons";
import "../../css/List/tabList.css";
import ListItem from "../Common-components/listItem";
import InfiniteScrollList from "../Common-components/InfiniteScrollList";
import { BASE_IMAGE_URL } from "../Common-components/constants";
import SearchGroup from "../Home/searchGroup";
import { ReactComponent as ArrowIcon } from '../../assets/icons/svg/arrowUnder.svg'
import Loading from '../Common-components/loading'

export default function TabList({ searchResults, tabResults }) {
  const [searchParams, setSearchParams] = useSearchParams(); // useSearchParams 사용
  const { setPageTitle } = usePageTitle(); // 모바일 header title 설정
  const [activeTab, setActiveTab] = useState("");
  const [localPageTitle, setLocalPageTitle] = useState(""); // 페이지 제목 상태
  const navigate = useNavigate();
  const [isTablet, setIsTablet] = useState(window.innerWidth <= 1327);
  const [isSortDropdownOpen, setIsSortDropdownOpen] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsTablet(window.innerWidth <= 1327);
    };
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // 👉 tabResults에서 탭 리스트 및 상품 매핑 구성
  const parsedTabs = tabResults?.data
    ? Object.entries(tabResults.data).map(([key, tab]) => ({
      id: key,
      label: tab.name,
      // tab.image_url null 이면 image_url : null 처리
      image_url: tab.image_url ? BASE_IMAGE_URL + tab.image_url : null,
      data: tab.products,
    }))
    : [];

  useEffect(() => {
    setLocalPageTitle("");
    const type = searchParams.get('type');
    const tabIdFromUrl = searchParams.get('tabId');

    if (type === 'region') {
      setLocalPageTitle("지역별 크루즈 일정");
      setPageTitle("지역별 크루즈 일정");
    } else if (type === 'line') {
      setLocalPageTitle("선사별 크루즈 일정");
      setPageTitle("선사별 크루즈 일정");
    }

    if (tabIdFromUrl) {
      setActiveTab(tabIdFromUrl);
    } else if (parsedTabs.length > 0 && !searchResults?.length) {
      setActiveTab(parsedTabs[0].id);
    }
  }, [tabResults, searchParams, setPageTitle, parsedTabs, searchResults]);

  const tabs = parsedTabs.map(({ id, label, image_url }) => ({ id, label, image_url }));
  // console.log(tabs)
  const tabData = {
    ...Object.fromEntries(parsedTabs.map(({ id, data }) => [id, data])),
  };

  const sortOptions = [
    { label: "빠른일자순", value: "fastest" },
    { label: "느린일자순", value: "slowest" },
    { label: "높은가격순", value: "highest" },
    { label: "낮은가격순", value: "lowest" },
  ];
  const [sortSelections, setSortSelections] = useState({});

  const activeSort = sortSelections[activeTab] || sortOptions[0].value;

  // sort 반환 결과
  const sortedTabData = useMemo(() => {
    const list = Array.isArray(tabData[activeTab]) ? [...tabData[activeTab]] : [];

    const parseDateValue = (value) => {
      if (!value) return Number.POSITIVE_INFINITY;
      const time = Date.parse(value);
      return Number.isNaN(time) ? Number.POSITIVE_INFINITY : time;
    };

    const parsePriceValue = (value) => {
      if (value === null || value === undefined) return Number.POSITIVE_INFINITY;
      const numeric = parseFloat(value);
      return Number.isNaN(numeric) ? Number.POSITIVE_INFINITY : numeric;
    };

    switch (activeSort) {
      case 'slowest':
        return list.sort((a, b) => parseDateValue(b?.startDate) - parseDateValue(a?.startDate));
      case 'highest':
        return list.sort((a, b) => parsePriceValue(b?.price) - parsePriceValue(a?.price));
      case 'lowest':
        return list.sort((a, b) => parsePriceValue(a?.price) - parsePriceValue(b?.price));
      case 'fastest':
      default:
        return list.sort((a, b) => parseDateValue(a?.startDate) - parseDateValue(b?.startDate));
    }
  }, [tabData, activeTab, activeSort]);

  const handleTabClick = (tabId) => {
    setActiveTab(tabId);
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('tabId', tabId);
    if (searchParams.get('type')) {
      newSearchParams.set('type', searchParams.get('type'));
    }
    setSearchParams(newSearchParams);

    // console.log("tabResults", tabResults);
    // console.log("parsedTabs", parsedTabs);
    // console.log("tabs", tabs);
    // console.log("activeTab", activeTab);
    // console.log("tabData[activeTab]", tabData[activeTab]);
  };

  if (!tabResults?.data) return <Loading isLoading />;

  return (
    <>
      <section className="tab-section">
        <div className="swipe-tabs">
          {localPageTitle && <div className="local-page-title-wrapper">
            <h2 className="local-page-title">{localPageTitle}</h2>
          </div>}
          <Swiper
            modules={[FreeMode]}
            slidesPerView="auto"
            freeMode
            grabCursor
            centeredSlides={false}
            slideToClickedSlide={false}
            spaceBetween={32}
            breakpoints={{
              651: { spaceBetween: 40 },
            }}
            className="tab-swiper"
          >
            {tabs.map((tab) => (
              <SwiperSlide key={tab.id} className="tab-slide">
                <button
                  className={`tab-button ${activeTab === tab.id ? "active" : ""}`}
                  onClick={() => handleTabClick(tab.id)}
                >
                  {tab.image_url && (
                    <img
                      src={tab.image_url}
                      alt={tab.name}
                      style={{ width: '16px', height: '16px', objectFit: 'contain' }}
                    />
                  )}
                  {tab.label}
                </button>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </section>
      {/* <section className="list-section"> */}
      <section className="list-section tab-list">
        <div className="tab-list-wrapper">
          <div className="filter-sort-container">
            <div className="filter-block">
              {/* <div className="filter-btn">
                <Filter className="filter-icon" /> 필터
              </div>
              <div className="filter-selected">적용된 필터 없음</div> */}
              {/* <SearchGroup onSearch={handleSearch} moTitle='상세 검색하기' forceMobile={true} /> */}
              <SearchGroup moTitle='상세 검색하기' forceMobile={true} />
            </div>
            <div className="sort-block">
              {isTablet ? (
                <div className="sort-dropdown">
                  <button
                    className="sort-dropdown-toggle"
                    onClick={() => setIsSortDropdownOpen(!isSortDropdownOpen)}
                  >
                    {sortOptions.find((option) => option.value === activeSort)?.label}
                    <ArrowIcon />
                  </button>
                  {isSortDropdownOpen && (
                    <ul className="sort-dropdown-menu">
                      {sortOptions.map((option) => (
                        <li
                          key={option.value}
                          className={`sort-dropdown-item ${activeSort === option.value ? "selected" : ""}`}
                          onClick={() => {
                            setSortSelections(prev => ({ ...prev, [activeTab]: option.value }));
                            setIsSortDropdownOpen(false);
                          }}
                        >
                          {option.label}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ) : (
                sortOptions.map((option) => (
                  <div
                    key={option.value}
                    className={`sort-option ${activeSort === option.value ? "selected" : ""}`}
                    onClick={() => setSortSelections(prev => ({ ...prev, [activeTab]: option.value }))}
                  >
                    {option.label}
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="tab-content">
            <InfiniteScrollList
              data={sortedTabData}
              renderItem={(item, index) => (
                <div key={index} className="tab-item">
                  <ListItem
                    item={item}
                  />
                </div>
              )}
            />
          </div>
        </div>
      </section>
    </>
  );
}
